/**
 * types.ts — Tipos para a Offline Write Queue (OWQ)
 *
 * Fila persistente em IndexedDB que sobrevive reload.
 * Quando online → drena para Drive. Quando offline → acumula.
 */

/** Tipos de arquivo que podem ser enfileirados */
export type OfflineFileKey =
  | 'referencias.json'
  | 'collections.json'
  | `highlights:${string}`
  | `project:${string}`
  | 'repositoryItems'
  | 'repositoryNucleos'
  | 'repositoryNotes'
  | 'userProfile'
  | 'scratchpadNotes'
  | 'namingConfig'
  | `destino:${string}`
  | `orientation:${string}`
  | `sharing:${string}`;

/** Tipo de operação — overwrite coalesce, delete-file não */
export type OfflineOperationType = 'overwrite' | 'delete-file';

/** Status de uma entrada na fila */
export type OfflineQueueEntryStatus = 'pending' | 'processing' | 'failed';

/** Entrada na fila offline — persistida no IndexedDB */
export interface OfflineQueueEntry {
  /** ID único (nanoid) */
  id: string;
  /** Chave do arquivo no Drive (usada para coalescing) */
  fileKey: string;
  /** overwrite = save completo do JSON, delete-file = deletar arquivo */
  operationType: OfflineOperationType;
  /** JSON stringified do estado completo (para overwrite) */
  payload: string;
  /** ETag do arquivo no momento do enqueue (para futuro merge 3-way) */
  baseEtag?: string;
  /** Estado do Drive antes da mutação (para futuro merge 3-way) */
  baseSnapshot?: string;
  /** ISO timestamp de criação */
  createdAt: string;
  /** Número de tentativas de drain */
  retries: number;
  /** Status atual */
  status: OfflineQueueEntryStatus;
}

/** Input para enqueue — campos auto-preenchidos pelo sistema */
export type OfflineQueueInput = Omit<OfflineQueueEntry, 'id' | 'createdAt' | 'retries' | 'status'>;

/** Resultado do drain */
export interface DrainResult {
  processed: number;
  failed: number;
  remaining: number;
}

/** Erro quando a fila está cheia */
export class OfflineQueueFullError extends Error {
  constructor(currentCount: number, maxCount: number) {
    super(`Fila offline cheia: ${currentCount}/${maxCount} operações. Reconecte para sincronizar.`);
    this.name = 'OfflineQueueFullError';
  }
}
